import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useDocumentHead } from "../../lib/document-head.ts";
import { useEventConfig } from "./api/useEventConfig.ts";
import { useKioskMode } from "./hooks/useKioskMode.ts";
import { LeadForm } from "./components/LeadForm.tsx";
import { Nav } from "../home/components/Nav.tsx";
import { SiteFooter } from "../home/components/SiteFooter.tsx";
import { useHomeMotion } from "../home/hooks/useHomeMotion.ts";
import type { EventConfig } from "./types.ts";
import {
  getEventContent,
  formatEventDay,
  formatTimeRange,
  type EventContent,
} from "./eventContent.ts";
import { IconCalendar, IconPin, IconTrophy, IconArrowUpRight } from "./components/eventIcons.tsx";

/**
 * Full-bleed dark page wrapper shared by every state of this screen. Uses the same
 * marketing header (Nav) and footer as the homepage; their SectionLinks client-navigate
 * back to the homepage section. `pt-16` offsets the fixed Nav so content clears it.
 */
function EventPage({ children }: { children: ReactNode }) {
  const { scrolled } = useHomeMotion();
  return (
    <div className="flex min-h-screen flex-col bg-ink font-archivo text-white">
      <Nav scrolled={scrolled} />
      <div className="flex-1 pt-[calc(4rem_+_var(--ann-bar-h,0px))]">{children}</div>
      <SiteFooter />
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-flame/[.12] text-flame">
        {icon}
      </span>
      <div>
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-white/45">{label}</p>
        <p className="font-semibold text-white">{value}</p>
      </div>
    </div>
  );
}

/**
 * Orchestrates the public event page: load event config → render the dark two-column
 * entry page / loading / error / closed. Bespoke marketing copy (schedule, venue,
 * headline) comes from eventContent.ts; the form itself is DB-driven (field_set).
 */
export function PublicFormScreen() {
  const { slug = "" } = useParams();
  const { source } = useKioskMode();
  const { data: event, isLoading, isError } = useEventConfig(slug);
  const content = getEventContent(slug);

  // Private entry page — keep out of search; title follows the loaded event.
  useDocumentHead({
    title: event?.name ? `${event.name} — RestoMag Events` : "Enter to win — RestoMag Events",
    robots: "noindex",
  });

  if (isLoading) {
    return (
      <EventPage>
        <div className="flex min-h-[60vh] items-center justify-center">
          <span
            className="h-7 w-7 animate-spin rounded-full border-2 border-white/30 border-t-flame"
            role="status"
            aria-label="Loading event…"
          />
        </div>
      </EventPage>
    );
  }

  if (isError) {
    return (
      <EventPage>
        <CenteredCard
          title="Couldn't load this event"
          message="Please check your connection and try again."
        />
      </EventPage>
    );
  }

  // null = not an open event (closed / archived / unknown — indistinguishable to anon).
  if (!event) {
    return (
      <EventPage>
        <CenteredCard
          title="This event isn't open"
          message="Entries are closed, or this link is no longer active."
        />
      </EventPage>
    );
  }

  // No bespoke content for this slug → simple centered dark card with the form.
  if (!content) {
    return (
      <EventPage>
        <main className="mx-auto w-full max-w-md px-5 py-10">
          <h1 className="text-2xl font-extrabold tracking-tight text-white">{event.name}</h1>
          {event.incentive_label && (
            <p className="mt-1 text-white/70">Enter to win {event.incentive_label}</p>
          )}
          {event.description && <p className="mt-2 text-sm text-white/55">{event.description}</p>}
          <div className="mt-6">
            <LeadForm event={event} source={source} />
          </div>
        </main>
      </EventPage>
    );
  }

  return (
    <EventPage>
      <FullEventPage event={event} content={content} source={source} />
    </EventPage>
  );
}

function FullEventPage({
  event,
  content,
  source,
}: {
  event: EventConfig;
  content: EventContent;
  source: "phone" | "kiosk";
}) {
  const day = formatEventDay(content.startsAt);
  const timeRange = formatTimeRange(content.startsAt, content.endsAt);
  const directionsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${content.venue} ${content.address}`,
  )}`;

  return (
    <>
      {/* Hero */}
      <section id="hero" className="relative scroll-mt-20">
        <div className="absolute inset-0 overflow-hidden">
          <img src="/eyekandy-openhouse.jpeg" alt="" className="h-full w-full object-cover opacity-[0.06]" />
        </div>
        {/*
          Below lg the hero is a single stacked column — cap it to a comfortable, centered
          width so it doesn't sprawl edge-to-edge on tablets / large phones. At lg it opens
          into the full two-column shell (content + form side by side).
        */}
        <div className="relative mx-auto grid w-full max-w-xl grid-cols-1 items-start gap-10 px-5 py-10 sm:py-12 md:px-8 md:py-16 lg:max-w-shell lg:grid-cols-2 lg:gap-14 lg:px-12">
          {/* Left column */}
          <div>
            <p className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.18em] text-flame sm:text-sm sm:tracking-[0.2em]">
              <span className="h-px w-6 bg-flame" />
              {content.occasion} · {content.year}
            </p>

            {/* Host attribution — makes clear whose event this is. */}
            <a
              href={content.host.url ?? undefined}
              target={content.host.url ? "_blank" : undefined}
              rel={content.host.url ? "noopener noreferrer" : undefined}
              className="mt-4 inline-flex items-center gap-3 no-underline"
              aria-label={
                content.host.url ? `${content.host.name} — visit their site` : content.host.name
              }
            >
              {content.host.icon ? (
                <span className="h-10 w-10 shrink-0 overflow-hidden rounded-full ring-1 ring-white/20 sm:h-11 sm:w-11">
                  <img src={content.host.icon} alt="" className="h-full w-full object-cover" />
                </span>
              ) : null}
              <span className="text-sm text-white/60 sm:text-base">
                Hosted by <span className="font-semibold text-white">{content.host.name}</span>
              </span>
            </a>

            <h1 className="mt-5 text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl sm:leading-[1.05] lg:text-6xl">
              {content.headlineLead} <span className="text-flame">{content.prize}</span>
            </h1>
            <p className="mt-5 text-base text-white/70 sm:text-lg lg:max-w-md">{content.blurb}</p>

            <div className="mt-8 space-y-4">
              <InfoRow icon={<IconCalendar size={16} />} label="When" value={`${day} · ${timeRange}`} />
              <InfoRow
                icon={<IconPin size={16} />}
                label="Where"
                value={
                  <a
                    href={directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${content.venue}, ${content.address} in Google Maps`}
                    className="inline-flex items-start gap-1 text-white underline decoration-flame/60 underline-offset-[3px] transition-colors hover:text-flame hover:decoration-flame"
                  >
                    <span>{content.venue} · {content.address}</span>
                    <IconArrowUpRight size={14} className="mt-[3px] shrink-0 text-flame" />
                  </a>
                }
              />
              <InfoRow icon={<IconTrophy size={16} />} label="Grand prize" value={content.prize} />
            </div>

            {content.schedule.length > 0 && (
              <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:p-5 lg:max-w-md">
                <p className="flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-flame">
                  <IconCalendar size={14} />
                  The schedule
                </p>
                <ul className="mt-4 space-y-3">
                  {content.schedule.map((item) => (
                    <li key={`${item.time}-${item.title}`} className="flex gap-3">
                      <span className="w-[5.5rem] shrink-0 text-[13px] font-semibold tabular-nums text-white/55 sm:w-28 sm:text-sm">
                        {item.time}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-white">{item.title}</span>
                        {item.detail && <span className="block text-xs text-white/55">{item.detail}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right column — entry form */}
          <div>
            <LeadForm event={event} source={source} />
          </div>
        </div>
      </section>

      {/* Visit us */}
      <section id="visit" className="scroll-mt-20 border-t border-white/[.06] bg-black py-16 sm:py-20">
        <div className="mx-auto max-w-shell px-5 md:px-8 lg:px-12">
          <div className="flex flex-col items-start justify-between gap-6 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8 md:flex-row md:items-center md:p-10">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">Visit us</h2>
              <p className="mt-3 text-lg font-semibold text-white">{content.venue}</p>
              <p className="text-white/60">{content.address}</p>
              <p className="mt-2 text-white/60">
                {day} · {timeRange}
              </p>
            </div>
            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white transition-colors hover:border-white/30 hover:bg-white/10 sm:w-auto md:shrink-0"
            >
              Get directions
              <IconArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

function CenteredCard({ title, message }: { title: string; message: string }) {
  return (
    <main className="mx-auto flex min-h-[60vh] w-full max-w-md flex-col items-center justify-center px-5 text-center">
      <h1 className="text-2xl font-bold tracking-tight text-white">{title}</h1>
      <p className="mt-2 text-white/60">{message}</p>
    </main>
  );
}
